/**
 * Tailwind-thema afgeleid van de design tokens. Elke kleur, radius en shadow verwijst
 * naar de CSS-variabele uit src/styles/tokens.css — nooit naar een hardgecodeerde waarde.
 */
module.exports = {
  theme: {
    extend: {
      /*
       * De breekpunten van de mockup, en niet die van Tailwind.
       *
       * `desk` 900 is de grens waar de medewerkerszone omslaat van de telefoon-indeling (onderbalk,
       * één kolom, de a11y-vloer uit base.css) naar het bureauscherm (zijbalk, rechterkolom,
       * guide-maten). `wide` 1100 schakelt de rechterkolom in, `full` 1400 het volle rooster met
       * acht snelkoppelingen en vier mededelingen naast elkaar.
       *
       * Tailwind's eigen `sm/md/lg` blijven bestaan; deze drie staan ernaast omdat de replica exact
       * deze getallen gebruikt (spec §12).
       */
      screens: {
        desk: '900px',
        wide: '1100px',
        full: '1400px',
      },
      colors: {
        brand: {
          primary: 'var(--brand-primary)',
          'primary-dark': 'var(--brand-primary-dark)',
          'primary-tint': 'var(--brand-primary-tint)',
          'primary-tint-deep': 'var(--brand-primary-tint-deep)',
          'dark-blue': 'var(--brand-dark-blue)',
          'dark-blue-tint': 'var(--brand-dark-blue-tint)',
          // De zijbalk (MRK-2): de donkere merkkleur, of de zijbalkkleur van de huisstijl.
          sidebar: 'var(--brand-sidebar)',
          cyan: 'var(--brand-cyan)',
          'cyan-tint': 'var(--brand-cyan-tint)',
          orange: 'var(--brand-orange)',
          'orange-soft': 'var(--brand-orange-soft)',
          'orange-tint': 'var(--brand-orange-tint)',
          'orange-deep': 'var(--brand-orange-deep)',
        },
        success: 'var(--success)',
        'success-tint': 'var(--success-tint)',
        warning: 'var(--warning)',
        'warning-tint': 'var(--warning-tint)',
        danger: 'var(--danger)',
        'danger-deep': 'var(--danger-deep)',
        // Tekst op een tint, en een rand die zwaar genoeg is voor een invoerveld (ONT-0).
        'success-tint-text': 'var(--success-tint-text-aa)',
        'danger-tint-text': 'var(--danger-tint-text-aa)',
        'border-strong': 'var(--border-strong)',
        'danger-tint': 'var(--danger-tint)',
        surface: 'var(--surface)',
        background: 'var(--background)',
        'background-warm': 'var(--background-warm)',
        'text-primary': 'var(--text-primary)',
        'text-secondary': 'var(--text-secondary)',
        'text-muted': 'var(--text-muted)',
        'text-on-dark': 'var(--text-on-dark)',
        border: 'var(--border)',
        'border-light': 'var(--border-light)',
        'sidebar-label': 'var(--sidebar-label)',
      },
      borderRadius: {
        /*
          De radius van de basiskaart (18 px), met een eigen naam.

          `rounded-card` werd in de bureauschermen gebruikt zonder dat de klasse bestond - Tailwind
          schrijft dan niets, en de kaarten stonden met scherpe hoeken. De gemeten replicacontrole
          van SCH-7 ving het. De naam blijft, want ze zegt wat ze bedoelt; nu betekent ze ook iets.
        */
        card: 'var(--radius-lg)',
        sm: 'var(--radius-sm)',
        md: 'var(--radius-md)',
        lg: 'var(--radius-lg)',
        xl: 'var(--radius-xl)',
        pill: 'var(--radius-pill)',
      },
      boxShadow: {
        sm: 'var(--shadow-sm)',
        md: 'var(--shadow-md)',
        lg: 'var(--shadow-lg)',
      },
      fontFamily: {
        heading: 'var(--font-heading)',
        body: 'var(--font-body)',
      },
      fontSize: {
        micro: ['10px', { lineHeight: '1.2', letterSpacing: '0.08em' }],
        caption: ['11px', { lineHeight: '1.45' }],
        small: ['12px', { lineHeight: '1.45' }],
        body: ['13px', { lineHeight: '1.5' }],
        content: ['14px', { lineHeight: '1.5' }],
        'title-sm': ['15px', { lineHeight: '1.3' }],
        'title-md': ['16px', { lineHeight: '1.3' }],
        'title-lg': ['18px', { lineHeight: '1.3' }],
        page: ['22px', { lineHeight: '1.3', letterSpacing: '-0.02em' }],
        hero: ['32px', { lineHeight: '1.2', letterSpacing: '-0.02em' }],
        // Maten met een rol: de waarde komt uit een CSS-variabele en verschilt per zone
        // (tokens.css voor het beheer, base.css voor de app). Gebruik deze in alles wat in
        // beide zones draait; de vaste maten hierboven blijven voor wat maar op één plek staat.
        'page-title': [
          'var(--type-page-title)',
          { lineHeight: 'var(--type-page-title-line)', letterSpacing: '-0.02em' },
        ],
        section: ['var(--type-section)', { lineHeight: 'var(--type-section-line)' }],
        'card-title': ['var(--type-card-title)', { lineHeight: 'var(--type-card-title-line)' }],
        copy: ['var(--type-copy)', { lineHeight: 'var(--type-copy-line)' }],
        support: ['var(--type-support)', { lineHeight: 'var(--type-support-line)' }],
        tab: ['var(--type-tab)', { lineHeight: 'var(--type-tab-line)', letterSpacing: '0.01em' }],
      },
      spacing: {
        card: 'var(--space-card)',
        'card-compact': 'var(--space-card-compact)',
        section: 'var(--space-section-gap)',
        element: 'var(--space-element-gap)',
        inline: 'var(--space-inline-gap)',
        'page-x': 'var(--space-page-x)',
        'page-y': 'var(--space-page-y)',
        touch: 'var(--touch-target-min)',
      },
      minHeight: { touch: 'var(--touch-target-min)' },
      minWidth: { touch: 'var(--touch-target-min)' },
    },
  },
};
